import React, { useState } from 'react';
import { ActionItem, actionsStore } from '../../services/actionsStore';
import { Badge } from '../ui/Badge';
import { User, Briefcase, Eye, Edit, Trash2, CheckCircle2, Circle } from 'lucide-react';
import { Tooltip } from '../ui/Tooltip';
import { useNotifications } from '../../contexts/NotificationContext';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { DatePicker } from '../ui/DatePicker';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
interface ActionsTableProps {
  actions: ActionItem[];
  showClient?: boolean;
  showResponsible?: boolean;
  onActionUpdate?: () => void;
}
export function ActionsTable({
  actions,
  showClient = false,
  showResponsible = false,
  onActionUpdate
}: ActionsTableProps) {
  const {
    addNotification
  } = useNotifications();
  const [selectedAction, setSelectedAction] = useState<ActionItem | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const handleToggleDone = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    actionsStore.toggleDone(id);
    if (onActionUpdate) onActionUpdate();
  };
  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm('Sei sicuro di voler eliminare questa azione?')) {
      actionsStore.delete(id);
      addNotification('success', 'Azione eliminata', "L'azione è stata eliminata con successo.");
      if (onActionUpdate) onActionUpdate();
    }
  };
  const handleView = (e: React.MouseEvent, action: ActionItem) => {
    e.stopPropagation();
    setSelectedAction(action);
    setViewModalOpen(true);
  };
  const handleEdit = (e: React.MouseEvent, action: ActionItem) => {
    e.stopPropagation();
    setSelectedAction(action);
    setEditModalOpen(true);
  };
  const handleSaveEdit = () => {
    setEditModalOpen(false);
    addNotification('success', 'Azione aggiornata', 'Le modifiche sono state salvate con successo.');
    if (onActionUpdate) onActionUpdate();
  };
  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  if (actions.length === 0) {
    return <div className="text-center py-8 text-slate-400" data-id="element-220">
        <p className="text-sm" data-id="element-221">Nessuna azione trovata</p>
      </div>;
  }
  return <>
      <div className="overflow-x-auto" data-id="element-222">
        <table className="w-full text-left border-collapse" data-id="element-223">
          <thead data-id="element-224">
            <tr className="bg-slate-50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold sticky top-0 z-10" data-id="element-225">
              <th className="px-4 py-3 bg-slate-50" data-id="element-226">Azione</th>
              <th className="px-4 py-3 bg-slate-50" data-id="element-227">Assegnatario</th>
              {showClient && <th className="px-4 py-3 bg-slate-50" data-id="element-228">Azienda</th>}
              <th className="px-4 py-3 bg-slate-50" data-id="element-229">Attività</th>
              <th className="px-4 py-3 bg-slate-50" data-id="element-230">Avanzamento</th>
              <th className="px-4 py-3 bg-slate-50" data-id="element-231">Scadenza</th>
              {showResponsible && <th className="px-4 py-3 bg-slate-50" data-id="element-232">Responsabile</th>}
              <th className="px-4 py-3 bg-slate-50 text-right" data-id="element-233">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-234">
            {actions.map(action => {
            const isOverdue = !action.isDone && new Date(action.deadline) < new Date();
            return <tr key={action.id} className={`group transition-colors ${action.isDone ? 'bg-slate-50/50 opacity-75' : isOverdue ? 'bg-red-50/30 hover:bg-red-50/60' : 'hover:bg-slate-50'}`} data-id="element-235">
                  <td className="px-4 py-3" data-id="element-236">
                    <span className={`text-sm font-medium ${action.isDone ? 'text-slate-500 line-through' : 'text-slate-900'}`} data-id="element-237">
                      {action.name}
                    </span>
                  </td>
                  <td className="px-4 py-3" data-id="element-238">
                    <div className="flex items-center gap-2" data-id="element-239">
                      {action.assigneeType === 'consulente' ? <User className="h-4 w-4 text-seform-orange flex-shrink-0" data-id="element-240" /> : <Briefcase className="h-4 w-4 text-blue-500 flex-shrink-0" data-id="element-241" />}
                      <span className={`text-sm ${action.isDone ? 'text-slate-500' : 'text-slate-700'}`} data-id="element-242">
                        {action.assigneeName}
                      </span>
                    </div>
                  </td>
                  {showClient && <td className="px-4 py-3" data-id="element-243">
                      <span className={`text-sm ${action.isDone ? 'text-slate-500' : 'text-slate-700'}`} data-id="element-244">
                        {action.clientName}
                      </span>
                    </td>}
                  <td className="px-4 py-3" data-id="element-245">
                    <Badge variant="neutral" size="sm" className={action.isDone ? 'opacity-70' : ''} data-id="element-246">
                      {action.activityProduct}
                    </Badge>
                  </td>
                  <td className="px-4 py-3" data-id="element-247">
                    <div className="flex flex-col gap-1" data-id="element-248">
                      <span className={`text-xs font-medium ${action.isDone ? 'text-slate-500' : 'text-seform-navy'}`} data-id="element-249">
                        {action.stepName}
                      </span>
                      <div className="flex items-center gap-2" data-id="element-250">
                        <div className="w-full bg-slate-200 rounded-full h-1.5 max-w-[80px]" data-id="element-251">
                          <div className={`h-1.5 rounded-full ${action.isDone ? 'bg-slate-400' : 'bg-seform-orange'}`} style={{
                        width: `${action.stepProgress}%`
                      }} data-id="element-252"></div>
                        </div>
                        <span className="text-xs text-slate-500" data-id="element-253">
                          {action.stepProgress}%
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-254">
                    <span className={`text-sm font-mono ${action.isDone ? 'text-slate-500' : isOverdue ? 'text-red-600 font-bold' : 'text-slate-600'}`} data-id="element-255">
                      {formatDate(action.deadline)}
                    </span>
                  </td>
                  {showResponsible && <td className="px-4 py-3" data-id="element-256">
                      <span className={`text-sm ${action.isDone ? 'text-slate-500' : 'text-slate-700'}`} data-id="element-257">
                        {action.responsible}
                      </span>
                    </td>}
                  <td className="px-4 py-3 text-right" data-id="element-258">
                    <div className="flex items-center justify-end gap-1" data-id="element-259">
                      <Tooltip content="Vedi dettagli" data-id="element-260">
                        <button onClick={e => handleView(e, action)} className="p-1.5 text-slate-500 hover:text-seform-navy rounded-md hover:bg-slate-100 transition-colors" data-id="element-261">
                          <Eye className="h-4 w-4" data-id="element-262" />
                        </button>
                      </Tooltip>
                      {!action.isDone && <Tooltip content="Modifica" data-id="element-263">
                          <button onClick={e => handleEdit(e, action)} className="p-1.5 text-seform-orange hover:text-seform-orange/80 rounded-md hover:bg-orange-50 transition-colors" data-id="element-264">
                            <Edit className="h-4 w-4" data-id="element-265" />
                          </button>
                        </Tooltip>}
                      <Tooltip content="Elimina" data-id="element-266">
                        <button onClick={e => handleDelete(e, action.id)} className="p-1.5 text-red-500 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors" data-id="element-267">
                          <Trash2 className="h-4 w-4" data-id="element-268" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      <Modal isOpen={viewModalOpen} onClose={() => setViewModalOpen(false)} title="Dettaglio Azione" size="lg" data-id="element-269">
        {selectedAction && <div className="space-y-6" data-id="element-270">
            <div className="grid grid-cols-2 gap-4" data-id="element-271">
              <div data-id="element-272">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-273">
                  Nome Azione
                </span>
                <p className="font-medium text-slate-900" data-id="element-274">
                  {selectedAction.name}
                </p>
              </div>
              <div data-id="element-275">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-276">
                  Stato
                </span>
                <div className="mt-1" data-id="element-277">
                  <Badge variant={selectedAction.isDone ? 'success' : 'warning'} data-id="element-278">
                    {selectedAction.isDone ? 'Completata' : 'Da fare'}
                  </Badge>
                </div>
              </div>
              <div data-id="element-279">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-280">
                  Assegnatario
                </span>
                <p className="font-medium text-slate-900 flex items-center gap-2 mt-1" data-id="element-281">
                  {selectedAction.assigneeType === 'consulente' ? <User className="h-4 w-4 text-seform-orange" data-id="element-282" /> : <Briefcase className="h-4 w-4 text-blue-500" data-id="element-283" />}
                  {selectedAction.assigneeName} ({selectedAction.assigneeType})
                </p>
              </div>
              <div data-id="element-284">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-285">
                  Cliente
                </span>
                <p className="font-medium text-slate-900" data-id="element-286">
                  {selectedAction.clientName}
                </p>
              </div>
              <div data-id="element-287">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-288">
                  Attività / Prodotto
                </span>
                <p className="font-medium text-slate-900" data-id="element-289">
                  {selectedAction.activityProduct}
                </p>
              </div>
              <div data-id="element-290">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-291">
                  Step
                </span>
                <p className="font-medium text-slate-900" data-id="element-292">
                  {selectedAction.stepName} ({selectedAction.stepProgress}%)
                </p>
              </div>
              <div data-id="element-293">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-294">
                  Scadenza
                </span>
                <p className="font-medium text-slate-900" data-id="element-295">
                  {formatDate(selectedAction.deadline)}
                </p>
              </div>
              <div data-id="element-296">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-297">
                  Responsabile
                </span>
                <p className="font-medium text-slate-900" data-id="element-298">
                  {selectedAction.responsible}
                </p>
              </div>
            </div>
            <div className="flex justify-between items-center pt-4 border-t border-slate-100" data-id="element-299">
              <button onClick={() => {
            actionsStore.toggleDone(selectedAction.id);
            setSelectedAction({
              ...selectedAction,
              isDone: !selectedAction.isDone
            });
            if (onActionUpdate) onActionUpdate();
          }} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedAction.isDone ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100' : 'bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100'}`} data-id="element-300">
                {selectedAction.isDone ? <CheckCircle2 className="h-4 w-4 text-green-600" data-id="element-301" /> : <Circle className="h-4 w-4 text-slate-400" data-id="element-302" />}
                {selectedAction.isDone ? 'Completata' : 'Segna come completata'}
              </button>
              <Button onClick={() => setViewModalOpen(false)} data-id="element-303">Chiudi</Button>
            </div>
          </div>}
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} title="Modifica Azione" size="lg" data-id="element-304">
        {selectedAction && <div className="space-y-6" data-id="element-305">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-306">
              <Input label="Nome Azione" defaultValue={selectedAction.name} className="col-span-full" data-id="element-307" />
              <Input label="Assegnatario" defaultValue={selectedAction.assigneeName} data-id="element-308" />
              <DatePicker label="Scadenza" value={selectedAction.deadline} onChange={() => {}} data-id="element-309" />
              <Textarea label="Note" placeholder="Inserisci eventuali note..." className="col-span-full" rows={3} data-id="element-310" />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-311">
              <Button variant="flat" onClick={() => setEditModalOpen(false)} data-id="element-312">
                Annulla
              </Button>
              <Button onClick={handleSaveEdit} data-id="element-313">Salva modifiche</Button>
            </div>
          </div>}
      </Modal>
    </>;
}